import axios from 'axios';
import axiosInstance, { publicAxiosInstance } from './AxiosService';

const OnboardingService = {

  getAllRegistrations: async () => {
    const response = await axiosInstance.get('registration/');
    return response.data;
  },

  getRegistrationById: async (registrationID) => {
    const response = await axiosInstance.get(`fetch-registration-id/${registrationID}/`);
    return response.data;
  },

  createRegistration: async (userData) => {
    try {
      console.log('Attempting to create registration with data:', userData);
      
      // Normalize email (trim whitespace and lowercase)
      const email = userData.EMAIL ? userData.EMAIL.trim().toLowerCase() : userData.EMAIL;
      const normalizedUserData = {
        ...userData,
        EMAIL: email
      };
      
      // First check if user exists
      const exists = await OnboardingService.checkExistingUser(email);
      console.log(`User exists check for ${email}: ${exists}`);
      
      if (exists) {
        console.log(`User with email ${email} already exists`);
        throw new Error('USER_EXISTS');
      }

      console.log('User does not exist, proceeding with registration with normalized data');
      // Use public instance for registration (no auth required)
      const response = await publicAxiosInstance.post('registration/', normalizedUserData);
      console.log('Registration successful:', response.data);
      return response.data;
    } catch (error) {
      if (error.message === 'USER_EXISTS') {
        throw error;
      }
      
      // Log the complete error details for debugging
      console.error('Error creating registration:', error);
      
      // Check if backend returned user exists error with debug info
      if (error.response && error.response.data) {
        console.error('Server error details:', error.response.data);
        
        // Check for debug info
        if (error.response.data.debug_info) {
          console.log('Debug info from backend:', error.response.data.debug_info);
          console.log(`Found in ALPHAREGISTRATION: ${error.response.data.debug_info.found_in_alpharegistration}`);
          console.log(`Found in User model: ${error.response.data.debug_info.found_in_user_model}`);
          console.log(`Total ALPHAREGISTRATION records: ${error.response.data.debug_info.total_alpharegistration_records}`);
        }
        
        if (error.response.data.detail === 'User already exists') {
          console.error('Backend reported user already exists');
          throw new Error('USER_EXISTS');
        }
        
        throw new Error(error.response.data.detail || 'Registration failed. Please try again.');
      }
      
      // Check if it's an authentication error
      if (error.response && error.response.status === 401) {
        console.error('Authentication error - endpoint requires authentication');
        throw new Error('Authentication required. Please contact support.');
      }
      
      throw new Error('Registration failed. Please try again.');
    }
  },

  sendRegistrationConfirmation: async (registrationData) => {
    try {
      // Use public instance for confirmation emails (no auth required)
      const response = await publicAxiosInstance.post('send-registration-confirmation/', registrationData);
      return response.data;
    } catch (error) {
      console.error('Error sending registration confirmation:', error);
      throw new Error('Failed to send confirmation. Please try again.');
    }
  },

  checkExistingUser: async (email) => {
    try {
      // Normalize email (trim whitespace and lowercase)
      const normalizedEmail = email ? email.trim().toLowerCase() : email;
      
      console.log(`Checking if user with normalized email ${normalizedEmail} exists...`);
      // Use public instance for checking existing users (no auth required)
      const response = await publicAxiosInstance.get('check-existing-user/', {
        params: { email: normalizedEmail }
      });
      
      console.log('Check existing user response:', response.data);
      
      // Check for debug info and log it
      if (response.data.debug_info) {
        console.log('Debug info from backend:', response.data.debug_info);
        console.log(`Found in ALPHAREGISTRATION: ${response.data.debug_info.found_in_alpharegistration}`);
        console.log(`Found in User model: ${response.data.debug_info.found_in_user_model}`);
        console.log(`ALPHAREGISTRATION total records: ${response.data.debug_info.alpharegistration_count || 'unknown'}`);
        
        // IMPORTANT: We only want to check ALPHAREGISTRATION, not User model
        // Return the specific ALPHAREGISTRATION check result rather than the combined result
        if (typeof response.data.debug_info.found_in_alpharegistration === 'boolean') {
          console.log('Using found_in_alpharegistration value:', response.data.debug_info.found_in_alpharegistration);
          return response.data.debug_info.found_in_alpharegistration;
        }
      }
      
      // Ensure the response has the expected structure
      if (response.data && typeof response.data.exists === 'boolean') {
        return response.data.exists;
      } else {
        console.error('Unexpected response format from check-existing-user:', response.data);
        return false; // Safer to assume user doesn't exist if API response is malformed
      }
    } catch (error) {
      console.error('Error checking existing user:', error);
      
      // Log more details about the error for debugging
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      
      // In case of error, don't block the registration process
      return false;
    }
  },

}

export default OnboardingService;
